import React from 'react';
import './VideoLightbox.css';

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function VideoLightbox({ items, currentIndex, onClose, onPrev, onNext }) {
  const videoRef = React.useRef(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [showControls, setShowControls] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(false);
  const [volume, setVolume] = React.useState(0.5);
  const controlsTimeoutRef = React.useRef(null);

  if (!items || items.length === 0) {
    return null;
  }

  const currentItem = items[currentIndex];

  // Handle video events
  const handleCanPlay = React.useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handlePlayEvent = React.useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePauseEvent = React.useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleEnded = React.useCallback(() => {
    setIsPlaying(false);
  }, []);

  const playVideo = React.useCallback((video) => {
    if (!video) return;

    let playPromise;
    try {
      playPromise = video.play();
    } catch (error) {
      console.error('Unable to start lecture playback:', error);
      return;
    }

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error('Unable to start lecture playback:', error);
      });
    }
  }, []);

  const handleStartPlayback = React.useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = false;
    video.volume = 0.5;
    setIsMuted(false);
    setVolume(0.5);
    playVideo(video);
  }, [playVideo]);

  // Handle mouse movement for controls visibility
  const handleMouseMove = React.useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 500);
  }, []);

  const handleMouseEnter = React.useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(true);
  }, []);

  // Audio controls
  const toggleMute = () => {
    if (videoRef.current) {
      // Read current muted state from video element to avoid stale closure
      const newMuted = !videoRef.current.muted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      // Update mute state based on actual volume
      setIsMuted(newVolume === 0);
      // Also ensure video element mute matches
      videoRef.current.muted = newVolume === 0;
    }
  };

  // Video seeking (forward/backward)
  const SEEK_DURATION = 10;

  const seekVideo = (offset) => {
    const video = videoRef.current;
    if (!video || video.readyState < 1 || !Number.isFinite(video.currentTime)) {
      return;
    }

    const duration = Number.isFinite(video.duration) ? video.duration : null;
    const targetTime = Math.max(0, video.currentTime + offset);
    const boundedTime = duration === null ? targetTime : Math.min(duration, targetTime);

    try {
      video.currentTime = boundedTime;
    } catch (error) {
      console.error('Failed to seek video:', error);
    }
  };

  const handleSeekBackward = () => {
    seekVideo(-SEEK_DURATION);
  };

  const handleSeekForward = () => {
    seekVideo(SEEK_DURATION);
  };

  // Set the initial audio state explicitly because volume is not a JSX media attribute.
  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.volume = 0.5;
    }
  }, []);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        // Check if Ctrl or Alt is pressed for seeking
        if (e.ctrlKey || e.altKey) {
          // Ctrl+ArrowLeft or Alt+ArrowLeft: seek back 10 seconds
          if (videoRef.current) {
            handleSeekBackward();
          }
        } else {
          // Just ArrowLeft: navigate to previous lecture
          onPrev();
        }
      } else if (e.key === 'ArrowRight') {
        // Check if Ctrl or Alt is pressed for seeking
        if (e.ctrlKey || e.altKey) {
          // Ctrl+ArrowRight or Alt+ArrowRight: seek forward 10 seconds
          if (videoRef.current) {
            handleSeekForward();
          }
        } else {
          // Just ArrowRight: navigate to next lecture
          onNext();
        }
      } else if (e.key === ' ') {
        e.preventDefault();
        // Toggle play/pause on spacebar
        if (videoRef.current) {
          if (videoRef.current.paused) {
            playVideo(videoRef.current);
            setIsPlaying(true);
          } else {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, playVideo]);

  // Auto-hide controls after 3 seconds on mount (if user doesn't interact)
  React.useEffect(() => {
    if (!isLoaded) return;
    const hideTimeout = setTimeout(() => {
      setShowControls(false);
    }, 3000);
    return () => clearTimeout(hideTimeout);
  }, [isLoaded, showControls]);

  // Auto-show controls when video loads
  React.useEffect(() => {
    if (isLoaded) {
      setShowControls(true);
    }
  }, [isLoaded]);

  // Pause when component unmounts
  React.useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  return (
    <div className="video-lightbox-overlay" onClick={onClose}>
      <div className="video-lightbox-container" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="video-lightbox-close"
          onClick={onClose}
          aria-label="Close video"
        >
          ×
        </button>

        <div className="video-lightbox-content">
          <div className="video-lightbox-header">
            <h2 className="video-title">
              {currentItem.title}
              {currentItem.language && (
                <span className="video-language-badge">({currentItem.language})</span>
              )}
            </h2>
            <p className="video-description">
              {currentItem.description || 'No description available'}
            </p>
          </div>

          <div
            className="video-lightbox-player"
            onContextMenu={(e) => e.preventDefault()}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={handleMouseEnter}
          >
            <video
              ref={videoRef}
              src={currentItem.videoUrl}
              preload="metadata"
              onCanPlay={handleCanPlay}
              onPlay={handlePlayEvent}
              onPause={handlePauseEvent}
              onEnded={handleEnded}
              muted={isMuted}
              playsInline
              webkit-playsinline
              controlsList="nodownload"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
            />
            <div className="video-overlay" />

            {!isPlaying && (
              <button
                type="button"
                className="video-start-button"
                onClick={handleStartPlayback}
                aria-label="Play lecture"
              >
                <span className="video-start-icon" aria-hidden="true" />
              </button>
            )}

            {/* Compact floating controls panel */}
            <div className={`video-controls-panel ${showControls ? 'visible' : ''}`}>
              <div className="controls-row">
                <button
                  type="button"
                  onClick={() => {
                    const video = videoRef.current;
                    if (video.paused) {
                      playVideo(video);
                      setIsPlaying(true);
                    } else {
                      video.pause();
                      setIsPlaying(false);
                    }
                  }}
                  aria-label={isPlaying ? 'Pause video' : 'Play video'}
                  title={isPlaying ? 'Pause video' : 'Play video'}
                  className="control-btn play-btn"
                >
                  {isPlaying ? '❚❚' : '▶'}
                </button>

                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    handleSeekBackward();
                  }}
                  aria-label="Rewind 10 seconds"
                  title="Rewind 10 seconds (←)"
                  className="control-btn seek-btn"
                >
                  ← 10
                </button>

                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    handleSeekForward();
                  }}
                  aria-label="Forward 10 seconds"
                  title="Forward 10 seconds (→)"
                  className="control-btn seek-btn"
                >
                  10 →
                </button>

                <div className="audio-controls">
                  <button
                    type="button"
                    onClick={toggleMute}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                    title={isMuted ? 'Unmute' : 'Mute'}
                    className="control-btn mute-btn"
                  >
                    {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                    aria-label="Volume"
                  />
                </div>
              </div>

              <div className="video-time-row">
                <span className="video-time">
                  {formatTime(videoRef.current?.currentTime || 0)}
                </span>
                <span className="video-time-separator">/</span>
                <span className="video-time">
                  {formatTime(videoRef.current?.duration || 0)}
                </span>
              </div>

              <div className="video-navigation-mini">
                <button
                  type="button"
                  onClick={onPrev}
                  disabled={currentIndex === 0}
                  aria-label="Previous lecture"
                  className="nav-btn"
                >
                  ‹
                </button>
                <span className="video-position">
                  {currentIndex + 1} / {items.length}
                </span>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={currentIndex === items.length - 1}
                  aria-label="Next lecture"
                  className="nav-btn"
                >
                  ›
                </button>
              </div>
            </div>
          </div>

        </div>
    </div>
  </div>

  );
}
